/**
 * SpotChatWidget
 *
 * Anonymous sales/support chat with Spot the mascot on Bootcamp course
 * pages. After five quiet seconds, Spot invites a question for five seconds.
 * Two more invitations follow after 30s and 60s quiet intervals, then stop.
 * Invitations wait while the visitor is busy or the tab is hidden.
 * Clicking opens a compact chat panel backed
 * by POST /public/spot-chat and stops further invitations for that visit.
 * The panel close control returns to the mascot launcher without clearing the
 * conversation. The launcher's separate close control dismisses the entire
 * widget for the current page lifetime; a browser refresh restores it.
 *
 * WHY: Visitors should not have to read the whole page to get answers.
 * No login required. The current rendered page is captured as normalized text,
 * links, and image descriptions when a question is sent. Raw HTML is never
 * sent to Node or the model.
 */

import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import spotCircle from '../../assets/spot-circle.png';
import { ANALYTICS_EVENTS, pushEvent } from '../../utils/analytics';
import {
  getSpotBubbleDelayMs,
  SpotPageSnapshot,
  SpotChatMessage,
  streamSpotChat,
} from './spotChatApi';
import './SpotChatWidget.css';

type UiMessage =
  | SpotChatMessage
  | { role: 'error'; content: string }
  | { role: 'assistant'; content: string; streaming?: boolean };

/** Pages where Spot appears and receives current rendered-page context. */
export const SPOT_ALLOWED_PATH_PREFIXES = ['/courses/ai-trading-bootcamp', '/courses-clarity'];

export function isSpotAllowedPath(pathname: string): boolean {
  return SPOT_ALLOWED_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

const normalizePageLines = (value: string): string[] => {
  return value
    .split(/\n+/)
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
};

const elementIsRendered = (element: Element): boolean => {
  if (
    element.closest(
      '[data-testid="spot-chat-widget"], [hidden], [aria-hidden="true"], script, style, noscript, template',
    )
  ) {
    return false;
  }
  if (!window.getComputedStyle) {
    return true;
  }
  let current: Element | null = element;
  while (current) {
    const style = window.getComputedStyle(current);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    current = current.parentElement;
  }
  return true;
};

/**
 * Capture what the visitor can read now, not the page HTML or a duplicated
 * course summary. Preserve repeated inline words and punctuation: text nodes
 * are fragments of sentences, so deduplicating them changes the page's meaning.
 */
export function collectVisiblePageSnapshot(): SpotPageSnapshot {
  if (!document.body) {
    return { text: '', links: [], imageDescriptions: [] };
  }

  const textWalker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
  const visibleTextParts: string[] = [];
  let textNode = textWalker.nextNode();
  while (textNode) {
    const parent = textNode.parentElement;
    if (parent && elementIsRendered(parent)) {
      visibleTextParts.push(textNode.textContent || '');
    }
    textNode = textWalker.nextNode();
  }
  const text = normalizePageLines(visibleTextParts.join('\n')).join('\n');

  const links = Array.from(document.querySelectorAll<HTMLAnchorElement>('a[href]'))
    .filter(elementIsRendered)
    .map((link) => ({
      label: (link.innerText || link.textContent || '').replace(/\s+/g, ' ').trim(),
      href: link.href,
    }))
    .filter((link) => link.label && link.href)
    .filter(
      (link, index, all) =>
        all.findIndex(
          (candidate) => candidate.label === link.label && candidate.href === link.href,
        ) === index,
    );

  const imageDescriptions = Array.from(document.querySelectorAll<HTMLImageElement>('img[alt]'))
    .filter(elementIsRendered)
    .map((image) => image.alt.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .filter((description, index, all) => all.indexOf(description) === index);

  return { text, links, imageDescriptions };
}

/**
 * Short questions that rotate through Spot's intermittent speech bubble.
 */
export const SPOT_BUBBLE_MESSAGES = [
  'How can I help you?',
  'Questions about the price or schedule?',
  'Wondering if this bootcamp is right for you?',
];

export const SPOT_BUBBLE_DISPLAY_MS = 5_000;
export const SPOT_BUBBLE_EXIT_MS = 380;
const SPOT_BUBBLE_QUIET_INTERVALS_MS = [30_000, 60_000];
const SPOT_BUBBLE_IDLE_MS = 2_000;

function createSessionId(): string {
  if (crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `spot-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/** Render simple markdown links without pulling in a full markdown library. */
function renderMessageContent(content: string): React.ReactNode {
  const parts = content.split(/(\[[^\]]+\]\([^)]+\))/g);
  return parts.map((part, index) => {
    const match = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (!match) {
      return <React.Fragment key={`t-${index}`}>{part}</React.Fragment>;
    }
    const [, label, href] = match;
    const safeHref =
      href.startsWith('/') || href.startsWith('https://') || href.startsWith('http://')
        ? href
        : '#';
    return (
      <a
        key={`a-${index}`}
        href={safeHref}
        target={safeHref.startsWith('/') ? undefined : '_blank'}
        rel={safeHref.startsWith('/') ? undefined : 'noreferrer'}
      >
        {label}
      </a>
    );
  });
}

export type SpotChatWidgetProps = {
  /** Override bubble delay for tests / experiments (ms). */
  bubbleDelayMs?: number;
  /** Force open for tests. */
  defaultOpen?: boolean;
  /** Force bubble visible immediately (tests). */
  forceBubble?: boolean;
};

export default function SpotChatWidget({
  bubbleDelayMs,
  defaultOpen = false,
  forceBubble = false,
}: SpotChatWidgetProps) {
  const location = useLocation();
  const titleId = useId();
  const [open, setOpen] = useState(defaultOpen);
  const [dismissed, setDismissed] = useState(false);
  const [bubbleVisible, setBubbleVisible] = useState(forceBubble);
  const [bubbleIndex, setBubbleIndex] = useState(0);
  const [bubbleLeaving, setBubbleLeaving] = useState(false);
  const [bubbleHovered, setBubbleHovered] = useState(false);
  const [launcherFocused, setLauncherFocused] = useState(false);
  const [pageHidden, setPageHidden] = useState(() => document.hidden);
  const [hasEngagedWithChat, setHasEngagedWithChat] = useState(defaultOpen);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<UiMessage[]>([
    {
      role: 'assistant',
      content: 'Hey, I am Spot. Ask me about BotSpot courses, pricing, or how to get started.',
    },
  ]);
  const [isSending, setIsSending] = useState(false);
  const sessionIdRef = useRef(createSessionId());
  const listRef = useRef<HTMLDivElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const questionCountRef = useRef(0);
  const launcherRef = useRef<HTMLButtonElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const returnFocusRef = useRef(false);
  const lastScrollRef = useRef(0);

  const hideOnPath = useMemo(
    () => !isSpotAllowedPath(location.pathname || ''),
    [location.pathname],
  );

  const delayMs = bubbleDelayMs ?? getSpotBubbleDelayMs();

  useEffect(() => {
    const recordScroll = () => {
      lastScrollRef.current = Date.now();
    };
    const recordVisibility = () => {
      setPageHidden(document.hidden);
      setBubbleLeaving(false);
    };
    window.addEventListener('scroll', recordScroll, { passive: true });
    document.addEventListener('visibilitychange', recordVisibility);
    return () => {
      window.removeEventListener('scroll', recordScroll);
      document.removeEventListener('visibilitychange', recordVisibility);
    };
  }, []);

  useEffect(() => {
    if (
      open ||
      dismissed ||
      hideOnPath ||
      hasEngagedWithChat ||
      bubbleVisible ||
      bubbleIndex >= SPOT_BUBBLE_MESSAGES.length
    )
      return undefined;

    let remaining = bubbleIndex === 0 ? delayMs : SPOT_BUBBLE_QUIET_INTERVALS_MS[bubbleIndex - 1];
    let startedAt = Date.now();
    let timer: number | undefined;

    const schedule = () => {
      if (document.hidden) return;
      startedAt = Date.now();
      timer = window.setTimeout(() => {
        timer = undefined;
        if (document.hidden) return;
        const editing = document.activeElement?.closest(
          'input, textarea, select, [contenteditable="true"]',
        );
        const dialogOpen = Array.from(
          document.querySelectorAll('[role="dialog"], dialog[open]'),
        ).some((dialog) => dialog.getClientRects().length > 0);
        const videoPlaying = Array.from(document.querySelectorAll('video')).some(
          (video) => !video.paused && !video.ended,
        );
        if (
          editing ||
          dialogOpen ||
          videoPlaying ||
          Date.now() - lastScrollRef.current < SPOT_BUBBLE_IDLE_MS
        ) {
          remaining = 1_000;
          schedule();
          return;
        }
        setBubbleVisible(true);
      }, remaining);
    };
    const onVisibilityChange = () => {
      if (document.hidden) {
        if (timer !== undefined) {
          remaining = Math.max(SPOT_BUBBLE_IDLE_MS, remaining - (Date.now() - startedAt));
          window.clearTimeout(timer);
          timer = undefined;
        }
      } else {
        schedule();
      }
    };
    schedule();
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [bubbleIndex, bubbleVisible, delayMs, dismissed, hasEngagedWithChat, hideOnPath, open]);

  // Keep prompts brief, then leave a meaningful quiet interval before the next.
  useEffect(() => {
    if (
      !bubbleVisible ||
      open ||
      hasEngagedWithChat ||
      bubbleHovered ||
      launcherFocused ||
      pageHidden ||
      hideOnPath ||
      dismissed
    )
      return undefined;
    const timers = [
      window.setTimeout(() => setBubbleLeaving(true), SPOT_BUBBLE_DISPLAY_MS),
      window.setTimeout(() => {
        setBubbleLeaving(false);
        setBubbleVisible(false);
        setBubbleIndex((index) => index + 1);
      }, SPOT_BUBBLE_DISPLAY_MS + SPOT_BUBBLE_EXIT_MS),
    ];
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [
    bubbleVisible,
    hasEngagedWithChat,
    open,
    bubbleHovered,
    launcherFocused,
    pageHidden,
    hideOnPath,
    dismissed,
  ]);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
    } else if (returnFocusRef.current) {
      launcherRef.current?.focus();
      returnFocusRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const openPanel = useCallback(() => {
    pushEvent({
      event: ANALYTICS_EVENTS.BOOTCAMP_CHAT_OPENED,
      source: location.pathname,
      page_search: location.search || undefined,
    });
    setHasEngagedWithChat(true);
    setOpen(true);
    setBubbleVisible(false);
    setBubbleLeaving(false);
    setBubbleHovered(false);
    setLauncherFocused(false);
  }, [location.pathname, location.search]);

  const closePanel = useCallback(() => {
    returnFocusRef.current = true;
    setOpen(false);
  }, []);

  const dismissWidget = useCallback(() => {
    setDismissed(true);
    setOpen(false);
    setBubbleVisible(false);
    abortRef.current?.abort();
    setIsSending(false);
  }, []);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || isSending) return;

    questionCountRef.current += 1;
    pushEvent({
      event: ANALYTICS_EVENTS.BOOTCAMP_CHAT_QUESTION_SUBMITTED,
      source: location.pathname,
      page_search: location.search || undefined,
      session_question_number: questionCountRef.current,
    });

    const historyForApi = messages
      .filter((m): m is SpotChatMessage => m.role === 'user' || m.role === 'assistant')
      .map((m) => ({ role: m.role, content: m.content }));

    setInput('');
    setMessages((prev) => [
      ...prev,
      { role: 'user', content: text },
      { role: 'assistant', content: '', streaming: true },
    ]);
    setIsSending(true);

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    let streamed = '';
    try {
      await streamSpotChat({
        message: text,
        history: historyForApi,
        pagePath: `${location.pathname}${location.search || ''}`,
        siteOrigin: window.location.origin,
        pageSnapshot: collectVisiblePageSnapshot(),
        sessionId: sessionIdRef.current,
        signal: controller.signal,
        handlers: {
          onDelta: (delta) => {
            streamed += delta;
            setMessages((prev) => {
              const next = [...prev];
              const last = next[next.length - 1];
              if (last && last.role === 'assistant') {
                next[next.length - 1] = {
                  role: 'assistant',
                  content: streamed,
                  streaming: true,
                };
              }
              return next;
            });
          },
          onFinal: (content) => {
            const finalText =
              (content || streamed).trim() || 'I am Spot. Ask me another question about BotSpot.';
            setMessages((prev) => {
              const next = [...prev];
              next[next.length - 1] = { role: 'assistant', content: finalText };
              return next;
            });
          },
          onError: (message) => {
            setInput((draft) => draft || text);
            setMessages((prev) => {
              const next = [...prev];
              // Replace empty streaming bubble with error.
              const lastMessage = next[next.length - 1];
              if (lastMessage?.role === 'assistant' && !lastMessage.content) {
                next[next.length - 1] = { role: 'error', content: message };
              } else {
                next.push({ role: 'error', content: message });
              }
              return next;
            });
          },
        },
      });
    } catch (error: unknown) {
      if (!(error instanceof Error && error.name === 'AbortError')) {
        setInput((draft) => draft || text);
        setMessages((prev) => [
          ...prev.filter(
            (m) => !(m.role === 'assistant' && 'streaming' in m && m.streaming && !m.content),
          ),
          {
            role: 'error',
            content: 'Spot is temporarily unavailable. Try again in a moment.',
          },
        ]);
      }
    } finally {
      setIsSending(false);
    }
  }, [input, isSending, location.pathname, location.search, messages]);

  if (hideOnPath || dismissed) {
    return null;
  }

  return (
    <div
      className="spot-chat-root"
      data-testid="spot-chat-widget"
      data-spot-open={open ? 'true' : 'false'}
    >
      {!open && (
        <div className="spot-chat-launcher-shell">
          <button
            type="button"
            className="spot-chat-launcher"
            ref={launcherRef}
            data-testid="spot-chat-launcher"
            aria-label="Chat with Spot"
            onClick={openPanel}
            onFocus={() => {
              setLauncherFocused(true);
              setBubbleLeaving(false);
            }}
            onBlur={() => setLauncherFocused(false)}
          >
            {bubbleVisible && (
              <span
                key={bubbleIndex}
                className={['spot-chat-bubble', bubbleLeaving ? 'spot-chat-bubble--leaving' : '']
                  .filter(Boolean)
                  .join(' ')}
                data-testid="spot-chat-bubble"
                onMouseEnter={() => {
                  setBubbleHovered(true);
                  setBubbleLeaving(false);
                }}
                onMouseLeave={() => setBubbleHovered(false)}
              >
                <span className="spot-chat-bubble__title">Spot</span>
                <span className="spot-chat-bubble__text">{SPOT_BUBBLE_MESSAGES[bubbleIndex]}</span>
              </span>
            )}
            <span className="spot-chat-avatar" aria-hidden="true">
              <img src={spotCircle} alt="" />
            </span>
          </button>
          <button
            type="button"
            className="spot-chat-dismiss"
            aria-label="Dismiss Spot chat until page refresh"
            data-testid="spot-chat-dismiss"
            onClick={dismissWidget}
          >
            <span
              className="spot-chat-dismiss__icon"
              data-testid="spot-chat-dismiss-icon"
              aria-hidden="true"
            >
              ×
            </span>
          </button>
        </div>
      )}

      {open && (
        <section
          className="spot-chat-panel"
          data-testid="spot-chat-panel"
          aria-labelledby={titleId}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.stopPropagation();
              closePanel();
            }
          }}
        >
          <header className="spot-chat-panel__header">
            <img src={spotCircle} alt="" />
            <div className="spot-chat-panel__header-copy">
              <h2 id={titleId} className="spot-chat-panel__title">
                Talk to Spot
              </h2>
              <p className="spot-chat-panel__subtitle">Ask about courses, pricing, and BotSpot</p>
            </div>
            <button
              type="button"
              className="spot-chat-panel__close"
              aria-label="Close Spot chat panel"
              data-testid="spot-chat-close"
              onClick={closePanel}
            >
              ×
            </button>
          </header>

          <div className="spot-chat-messages" ref={listRef} data-testid="spot-chat-messages">
            {messages.map((msg, index) => (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={`${msg.role}-${index}`}
                className={[
                  'spot-chat-msg',
                  msg.role === 'user'
                    ? 'spot-chat-msg--user'
                    : msg.role === 'error'
                      ? 'spot-chat-msg--error'
                      : 'spot-chat-msg--assistant',
                ].join(' ')}
              >
                {msg.content ? (
                  renderMessageContent(msg.content)
                ) : msg.role === 'assistant' ? (
                  <span
                    className="spot-chat-thinking"
                    data-testid="spot-chat-thinking"
                    aria-label="Spot is thinking"
                  >
                    <span className="spot-chat-thinking__label">Spot is thinking</span>
                    <span className="spot-chat-thinking__dots" aria-hidden="true">
                      <span />
                      <span />
                      <span />
                    </span>
                  </span>
                ) : (
                  ''
                )}
              </div>
            ))}
          </div>

          <form
            className="spot-chat-composer"
            onSubmit={(event) => {
              event.preventDefault();
              void sendMessage();
            }}
          >
            <textarea
              ref={inputRef}
              data-testid="spot-chat-input"
              value={input}
              rows={2}
              placeholder="Ask Spot anything about BotSpot…"
              disabled={isSending}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  void sendMessage();
                }
              }}
            />
            <button
              type="submit"
              className="spot-chat-composer__send"
              data-testid="spot-chat-send"
              disabled={isSending || !input.trim()}
              title={!input.trim() ? 'Type a question first' : 'Send to Spot'}
            >
              {isSending ? '…' : 'Send'}
            </button>
          </form>
        </section>
      )}
    </div>
  );
}
